import * as React from 'react'
import { Dialog } from 'primereact/dialog'
import { Calendar, CalendarBaseProps } from 'primereact/calendar'
import { addLocale } from 'primereact/api'
import moment from 'moment'

/**
 * TODO: 이걸 기본으로 -> InputCalrendar, CalendarSelect를 변경해야함...
* @description
    - id: label이 있는경우 필수 추가, 그외에는 필요에 따라 사용
    - label: label이 필요한 경우 텍스트 추가
    - placeholder: 필요한 경우 텍스트 추가
    - title: label 없는 경우 필수 추가
    - helpId: 도움말이 있는 경우 input과 연결을 위해 추가
    - helpMsg: 도움말 텍스트 추가
    - result: 에러 or 성공 체크 문자열 값으로 false or true 넣어줌
    - resultId: 에러 or 성공 문구가 필요한 경우 input과 연결을 위해 추가
    - resultMsg: 에러 or 성공 문구 텍스트 추가
    - disabled: disabled 핸들
    - readOnly: readOnly 핸들 
    - className: 기본 형태에서 추가 작업시 클래스명 추가 제일 상위에 추가됨
*/

export type DialogCalendarProps = Omit<CalendarBaseProps, 'onVisibleChange'> & {
    /**
     * true인 경우 과거 조회 용으로 '오늘' 혹은 today 이후로 선택 못하게 함.
     */
    isPastOnly?: boolean
    /**
     * true인 경우 미래 조회 용으로 '오늘' 혹은 today를 포함한 과거를 선택 못하게 함
     * 우선 DialogCalendar에만 사용되어 추가.
     */
    isFutureOnly?: boolean
    /**
     * 선택한 날짜, 없는 경우 today or 로컬 시간
     */
    value?: Date
    /**
     * 달력 다이얼로그 상단 제목
     */
    title?: string
    /**
     * 특정 날짜를 오늘로 지정하는 경우 사용
     */
    today?: Date
    /**
     * 날짜를 변경하면 발생하는 콜백
     */
    onChange?(value?: Date): void
    /**
     * 캘린더가 다이얼로그에 따라 보여지는 여부가 결정되어 별도로 분리
     */
    onVisibleChange?(value: boolean): void
    /**
     * 캘린더 선택 시 선택날짜 validate함수
     * true : 셋팅, false : 반려
     */
    validate?: (value: Date | null) => boolean
    validateFn?: () => void
}

export type DialogCalendarRef = {
    /**
     * 달력을 표시한다.
     */
    show(value: boolean): void
}
export const DialogCalendar = React.forwardRef<
    DialogCalendarRef,
    DialogCalendarProps
>((props, ref) => {
    let {
        minDate = new Date(1950, 0, 1),
        maxDate,
        isPastOnly = false,
        isFutureOnly = false,
        today = new Date(),
        value,
        title = '',
        visible = false,
        validate = () => true,
        validateFn,
        onChange,
        onVisibleChange,
        ...rest
    } = props

    React.useImperativeHandle(
        ref,
        () => ({
            show: (value: boolean) => {
                setDialogVisible(value)
            },
        }),
        []
    )

    if (isPastOnly) {
        //오늘을 포함한 과거만 선택한다.
        const preMaxDate = today
        if (maxDate) {
            //오늘과 maxDate 중에서 작은 날짜로 시정한다.
            maxDate = new Date(Math.min(Number(preMaxDate), Number(maxDate)))
        } else {
            maxDate = today
        }
    }
    if (isFutureOnly) {
        //오늘을 제외한 미래만 선택한다.
        const preMinDate = new Date(today)
        preMinDate.setDate(preMinDate.getDate() + 1)
        if (minDate) {
            minDate = new Date(Math.max(Number(preMinDate), Number(minDate)))
        } else {
            minDate = preMinDate
        }
    }
    addLocale('ko', {
        firstDayOfWeek: 0,
        dayNames: [
            '일요일',
            '월요일',
            '화요일',
            '수요일',
            '목요일',
            '금요일',
            '토요일',
        ],
        dayNamesShort: ['일', '월', '화', '수', '목', '금', '토'],
        dayNamesMin: ['일', '월', '화', '수', '목', '금', '토'],
        monthNames: [
            '1월',
            '2월',
            '3월',
            '4월',
            '5월',
            '6월',
            '7월',
            '8월',
            '9월',
            '10월',
            '11월',
            '12월',
        ],
        monthNamesShort: [
            '1월',
            '2월',
            '3월',
            '4월',
            '5월',
            '6월',
            '7월',
            '8월',
            '9월',
            '10월',
            '11월',
            '12월',
        ],
        today: '오늘',
        clear: '지우기',
        dateFormat: 'yy.mm.dd',
    })

    const calendarRef = React.useRef<HTMLDivElement>(null)
    const observerRef = React.useRef<MutationObserver>(null)
    const [dialogVisible, setDialogVisible] = React.useState(false)
    const [selectedDate, setSelectedDate] = React.useState<Date>(
        value ?? minDate ?? today
    )

    React.useEffect(() => {
        if (value) {
            setSelectedDate(value)
        }
    }, [value])

    // 날짜선택시
    const handleDateSelect = (date: Date | null) => {
        if (date) {
            if (validate(date)) {
                setSelectedDate(date)
                onChange(date)
            } else {
                validateFn?.()
            }
            onVisibleChange?.(false)
            setDialogVisible(false)

            domChangeDetect() // 변경감지
        }
    }

    // 년,월 타이틀 순서변경
    const calendarTitleSort = React.useCallback(() => {
        const title = calendarRef.current?.querySelector('.p-datepicker-title')
        if (title) {
            const year = title.querySelector('.p-datepicker-year')
            const month = title.querySelector('.p-datepicker-month')
            if (year !== null && month !== null) {
                year.setAttribute('aria-label', `${year.textContent}년`)
                month.setAttribute('aria-label', month.textContent)
            }
            if (month && year && month.nextElementSibling === year) {
                title.insertBefore(year, month)
            }
        }
    }, [title])

    // header 토,일 색지정
    const tableHeaderWeek = () => {
        const headers = calendarRef.current?.querySelectorAll(
            '.p-datepicker-calendar th span'
        )
        headers.forEach((item) => {
            const text = item.textContent?.trim() || ''
            if (text === '토') item.classList.add('saturday')
            if (text === '일') item.classList.add('sunday')
        })
    }

    // body 토,일 표시 색지정
    const customDayTemplate = (obj) => {
        let date = new Date(obj.year, obj.month, obj.day)
        if (date.getDay() === 0) {
            return <span className="sunday">{obj.day}</span>
        } else if (date.getDay() === 6) {
            return <span className="saturday">{obj.day}</span>
        } else {
            return obj.day
        }
    }

    // 접근성
    const pickerA11ySetup = () => {
        const dayLabels = calendarRef.current?.querySelectorAll(
            '[data-pc-section="daylabel"]'
        )
        dayLabels.forEach((item) => item.setAttribute('role', 'button'))
        const btnPrev = calendarRef.current?.querySelector('.p-datepicker-prev')
        btnPrev.setAttribute('aria-label', '이전달')
        const btnNext = calendarRef.current?.querySelector('.p-datepicker-next')
        btnNext.setAttribute('aria-label', '다음달')
    }

    // dialog 안으로 포함버전 초기설정
    const dialogSetup = () => {
        calendarTitleSort()
        tableHeaderWeek()
        pickerA11ySetup()
    }

    // dom변경시
    const domChangeDetect = () => {
        const observer = new MutationObserver(() => {
            calendarTitleSort()
            tableHeaderWeek()
            pickerA11ySetup()
        })
        observer.observe(calendarRef.current, {
            childList: true,
            subtree: true,
        })
        observerRef.current = observer
        return () => observer.disconnect()
    }

    React.useEffect(() => {
        setDialogVisible(visible)
    }, [visible])

    return (
        <Dialog
            draggable={false}
            header={<span tabIndex={0}>{title}</span>}
            visible={dialogVisible}
            onShow={() => {
                dialogSetup()
                domChangeDetect()
            }}
            onHide={() => {
                setDialogVisible(false)
                onVisibleChange?.(false)
            }}
            closable={true}
            ariaCloseIconLabel={`${title} 창닫기`}
            dismissableMask
            className="bottom-sheet"
        >
            <div ref={calendarRef}>
                <Calendar
                    {...rest}
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={(e) => {
                        handleDateSelect(e.value)
                    }}
                    value={selectedDate}
                    // 아래는 외부 값과 상관없이 고정됨.
                    locale="ko"
                    dateTemplate={customDayTemplate}
                    inline
                />
            </div>
        </Dialog>
    )
})
